/* eslint-disable max-classes-per-file */
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { GRAPHQL_ENDPOINT_PATH, HOME_SLUG, Language } from '@pai/commons'
import { DocumentNode, Kind, OperationDefinitionNode, print } from 'graphql'
import { map, Observable, OperatorFunction, retry, shareReplay, tap, timer } from 'rxjs'

import { RoutePath } from 'app/static/route-path.enum'

import { CLIENT_CONFIG } from '../client-config/client-config.token'
import { CURRENT_LANG } from '../i18n/language.token'
import { LoggerService } from '../logger/logger.service'
import {
  BaseResponse,
  BaseVariables,
  Footer,
  Header,
  JsonLdOrganization,
  Page_Where,
  PageDto,
  PageResponse,
  PageVariables,
  Project_Where,
  ProjectCardItem,
  ProjectCardsResponse,
  ProjectCardsVariables,
  ProjectDto,
  ProjectResponse,
  ProjectVariables,
  Publication_Where,
  PublicationCardItem,
  PublicationCardsResponse,
  PublicationCardsVariables,
  PublicationDto,
  PublicationResponse,
  PublicationVariables,
  TranslationsResponse,
  TranslationsVariables,
} from './generated/api-types'
import { PREVIEW_TOKEN } from './preview-token.token'
import { QUERY_BASE_DATA } from './requests/base-data.gql'
import { QUERY_PROJECT_CARDS, QUERY_PUBLICATION_CARDS } from './requests/filter-items.gql'
import { QUERY_PAGE } from './requests/page.gql'
import { QUERY_PROJECT } from './requests/project.gql'
import { QUERY_PUBLICATION } from './requests/publication.gql'
import { QUERY_TRANSLATIONS } from './requests/translations.gql'

export interface Links {
  [id: string]: `${Language}/${string}`
}

export class CmsApiError extends Error {
  readonly queryName: string
  constructor(queryName: string, message: string) {
    super(`${queryName}::${message}`)
    this.name = 'CmsApiError'
    this.queryName = queryName
  }
}

function throwIfNull<T>(queryName: string): OperatorFunction<T | null, T> {
  return map((value) => {
    if (value === null) {
      throw new CmsApiError(queryName, `Received unexpected null value from CMS API`)
    }
    return value
  })
}

export interface PagedResult<T> {
  readonly cards: readonly T[]
  readonly page: number
  readonly totalPages: number
  readonly totalDocs: number
}

@Injectable({ providedIn: 'root' })
export class CmsService {
  private readonly logger = inject(LoggerService).getInstance('CmsService')
  private readonly language = inject(CURRENT_LANG)
  private readonly previewToken = inject(PREVIEW_TOKEN)
  private readonly apiUrl = inject(CLIENT_CONFIG).cmsOrigin + GRAPHQL_ENDPOINT_PATH
  private readonly http = inject(HttpClient)

  private baseData = this.request<BaseResponse, BaseVariables>(QUERY_BASE_DATA, { locale: this.language }).pipe(
    shareReplay(1),
  )

  get links(): Observable<Links> {
    return this.baseData.pipe(
      map((data): Links => {
        return Object.fromEntries([
          ...(data.Pages?.docs.map((p) => [
            p.id,
            p.slug === HOME_SLUG ? `/${this.language}` : `/${this.language}/${p.slug}`,
          ]) ?? []),
          ...(data.Projects?.docs.map((p) => [p.id, `/${this.language}/${RoutePath.PROJECTS}/${p.slug}`]) ?? []),
          ...(data.Publications?.docs.map((p) => [p.id, `/${this.language}/${RoutePath.PUBLICATIONS}/${p.slug}`]) ??
            []),
        ]) as Links
      }),
      throwIfNull('Pages,Projects,Publications'),
    )
  }
  get header(): Observable<Header> {
    return this.baseData.pipe(
      map((data) => data.Header),
      throwIfNull('Header'),
    )
  }
  get footer(): Observable<Footer> {
    return this.baseData.pipe(
      map((data) => data.Footer),
      throwIfNull('Footer'),
    )
  }
  get organisation(): Observable<JsonLdOrganization> {
    return this.baseData.pipe(
      map((data) => data.JsonLdOrganization),
      throwIfNull('JsonLdOrganization'),
    )
  }

  translations(): Observable<Record<string, string>> {
    this.logger.debug('fetching translations', { locale: this.language })

    return this.request<TranslationsResponse, TranslationsVariables>(QUERY_TRANSLATIONS, {
      locale: this.language,
    }).pipe(
      map(
        (v): Record<string, string> =>
          Object.fromEntries(
            v.Translations?.docs
              .map(({ key, value }) => [key, value] as const)
              .filter((entry): entry is [string, string] => !!entry[0] && !!entry[1]) ?? [],
          ),
      ),
    )
  }

  getPage(slug: string, draft = false): Observable<PageDto | null> {
    // when slug is empty, we are fetching the home page
    slug ||= HOME_SLUG

    this.logger.debug('getPage', { slug, draft })
    const vars: PageVariables = {
      locale: this.language,
      draft,
      where: {
        slug: { equals: slug },
        ...(draft ? {} : ({ _status: { equals: 'published' } } satisfies Page_Where)),
      },
    }

    return this.request<PageResponse, PageVariables>(QUERY_PAGE, vars, draft ? this.previewToken : null).pipe(
      map((res) => res.Pages?.docs[0] ?? null),
      tap((result) => this.logger.debug('fetched page', { slug, draft, withResult: !!result })),
    )
  }

  getProject(slug: string, draft = false): Observable<ProjectDto | null> {
    this.logger.debug('getProject', { slug, draft })
    const vars: ProjectVariables = {
      locale: this.language,
      draft,
      where: {
        slug: { equals: slug },
        ...(draft ? {} : ({ _status: { equals: 'published' } } satisfies Project_Where)),
      },
    }
    return this.request<ProjectResponse, ProjectVariables>(QUERY_PROJECT, vars, draft ? this.previewToken : null).pipe(
      map((res) => res.Projects?.docs[0] ?? null),
      tap((result) => this.logger.debug('fetched project', { slug, draft, withResult: !!result })),
    )
  }

  getPublication(slug: string, draft = false): Observable<PublicationDto | null> {
    this.logger.debug('getPublication', { slug, draft })
    const vars: PublicationVariables = {
      locale: this.language,
      draft,
      where: {
        slug: { equals: slug },
        ...(draft ? {} : ({ _status: { equals: 'published' } } satisfies Publication_Where)),
      },
    }

    return this.request<PublicationResponse, PublicationVariables>(
      QUERY_PUBLICATION,
      vars,
      draft ? this.previewToken : null,
    ).pipe(
      map((res) => res.Publications?.docs[0] ?? null),
      tap((result) => this.logger.debug('fetched publication', { slug, draft, withResult: !!result })),
    )
  }

  /**
   * fetch project cards with pagination and filtering
   * @param filter array of slugs of {@link ProjectCategory}s to filter by
   * @param page 0-based page index
   * @param itemsPerPage number of items per page
   */
  getProjectCards(filter: string[], page: number, itemsPerPage: number): Observable<PagedResult<ProjectCardItem>> {
    this.logger.debug('getProjectCards', { filter, page, itemsPerPage })

    return this.request<ProjectCardsResponse, ProjectCardsVariables>(QUERY_PROJECT_CARDS, {
      locale: this.language,
      limit: itemsPerPage,
      page,
      categories:
        filter.length > 0
          ? {
              // issue in payload (see graphql-codegen.config.ts)
              in: filter as unknown as Array<object & {}>,
            }
          : undefined, // null won't work here
    }).pipe(
      map((r): PagedResult<ProjectCardItem> => {
        if (!r.Projects) {
          throw new Error('No Projects data received')
        }
        return {
          cards: r.Projects.docs ?? [],
          page: r.Projects.page,
          totalPages: r.Projects.totalPages,
          totalDocs: r.Projects.totalDocs,
        }
      }),
    )
  }

  /**
   * fetch publication cards with pagination and filtering
   * @param filter array of slugs of {@link PublicationCategory}s to filter by
   * @param page 0-based page index
   * @param itemsPerPage number of items per page
   */
  getPublicationCards(
    filter: string[],
    page: number,
    itemsPerPage: number,
  ): Observable<PagedResult<PublicationCardItem>> {
    this.logger.debug('getPublicationCards', { filter, page, itemsPerPage })

    return this.request<PublicationCardsResponse, PublicationCardsVariables>(QUERY_PUBLICATION_CARDS, {
      locale: this.language,
      limit: itemsPerPage,

      page,
      categories:
        filter.length > 0
          ? {
              // issue in payload (see graphql-codegen.config.ts)
              in: filter as unknown as Array<object & {}>,
            }
          : undefined, // null won't work here
    }).pipe(
      map((r): PagedResult<PublicationCardItem> => {
        if (!r.Publications) {
          throw new Error('No Publications data received')
        }
        return {
          cards: r.Publications.docs ?? [],
          page: r.Publications.page,
          totalPages: r.Publications.totalPages,
          totalDocs: r.Publications.totalDocs,
        }
      }),
    )
  }

  private request<TResponse, TVariables = never>(
    query: DocumentNode,
    variables: TVariables,
    previewToken: string | null = null,
  ): Observable<TResponse> {
    const queryName =
      query.definitions
        .filter((d): d is OperationDefinitionNode => d.kind === Kind.OPERATION_DEFINITION)
        .map((d) => d.name?.value ?? 'UnnamedQuery')
        .at(0) ?? 'no-op-found'

    const t0 = performance.now()
    this.logger.debug('make request', { queryName, variables })
    return this.http
      .post<{
        data: TResponse
        errors?: any[]
      }>(
        this.apiUrl,
        { query: print(query), variables },
        { headers: previewToken ? { 'x-preview-token': previewToken } : {} },
      )
      .pipe(
        retry({
          count: 3,
          delay: (error: unknown, retryCount: number) => {
            if (error instanceof HttpErrorResponse && error.status === 0) {
              this.logger.error('Network Error', error, { queryName, retryCount })
              return timer(100)
            }
            // Don't retry for status-zero errors
            throw new CmsApiError(queryName, error instanceof Error ? error.message : `Unknown error: ${String(error)}`)
          },
        }),
        map((response) => {
          if (response.errors) {
            const errorMessage =
              response.errors[0] && typeof response.errors[0] === 'object' && 'message' in response.errors[0]
                ? String((response.errors[0] as Record<string, unknown>).message)
                : 'Unknown GraphQL error'
            throw new CmsApiError(queryName, errorMessage)
          }
          return response.data
        }),
        tap(() => this.logger.debug('request successful', { queryName, duration: performance.now() - t0 })),
      )
  }
}
